import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase-config";

/**
 * Where the renderer resolves the report's root-relative URLs (sticker images,
 * and the built stylesheet in production).
 *
 * Sticker artwork totals over 2MB, far too much to inline into the request, so
 * the renderer fetches it over the network instead. In dev that cannot be
 * localhost -- the function has no route back to your machine -- so local
 * testing borrows the deployed origin, whose assets are identical.
 */
const assetBaseUrl = () =>
  import.meta.env.DEV ? "https://teachers.preschool.bee-happy.club/" : `${window.location.origin}/`;

/**
 * Collects every applied CSS rule as text.
 *
 * Read from the CSSOM rather than from <style> elements' contents, because
 * emotion -- which is what every MUI `sx` prop compiles to -- switches to
 * `insertRule()` in production builds. Its <style> tags are then *empty* in the
 * DOM, so copying outerHTML yields a document with no styling at all: huge
 * unsized images and unformatted text. Reading `cssRules` captures emotion's
 * runtime styles and the bundled stylesheet alike, in dev and production.
 */
const collectCss = (): string => {
  const rules: string[] = [];
  const imports: string[] = [];

  for (const sheet of Array.from(document.styleSheets)) {
    try {
      rules.push(Array.from(sheet.cssRules).map((rule) => rule.cssText).join("\n"));
    } catch {
      // A cross-origin sheet's rules cannot be read; reference it by URL and
      // let the renderer fetch it instead.
      if (sheet.href) imports.push(`@import url("${sheet.href}");`);
    }
  }

  // @import is only valid ahead of every other rule.
  return [...imports, ...rules].join("\n");
};

/**
 * Serialises the live report into a standalone document for server-side
 * rendering.
 *
 * Child photos are left as absolute Google Drive URLs for the renderer to
 * fetch -- the browser cannot read them (no CORS), which is why rendering
 * happens server-side at all.
 */
export const serialiseReport = (element: HTMLElement): string => {
  const head = `<style>${collectCss()}</style>`;

  return [
    "<!doctype html>",
    '<html lang="en">',
    "<head>",
    '<meta charset="utf-8">',
    head,
    // Chromium's print path needs the page itself to be white, not transparent.
    "<style>body{margin:0;background:#fff;}</style>",
    "</head>",
    "<body>",
    element.outerHTML,
    "</body>",
    "</html>",
  ].join("\n");
};

export interface ReportDraftResult {
  draftId: string;
  to: string;
  filename: string;
  pdfBytes: number;
}

/**
 * Renders the report to PDF and leaves it as a draft in info@bee-happy.club's
 * mailbox. Never sends -- the draft is reviewed and sent by hand.
 *
 * subject and body may contain <CHILD>, which is replaced server-side with the
 * child's first name. When omitted, the function falls back to its own
 * default email template.
 */
export const createReportDraft = async (
  childId: string,
  reportElement: HTMLElement,
  subject?: string,
  body?: string
): Promise<ReportDraftResult> => {
  const callable = httpsCallable<
    { childId: string; html: string; baseUrl: string; subject?: string; body?: string },
    ReportDraftResult
  >(functions, "createReportDraft");

  const result = await callable({
    childId,
    html: serialiseReport(reportElement),
    baseUrl: assetBaseUrl(),
    ...(subject ? { subject } : {}),
    ...(body ? { body } : {}),
  });

  return result.data;
};
